import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import AnimatedNumber from "@/components/AnimatedNumber";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Linkedin, Mail, ArrowUpRight, Sparkles, Briefcase, Target,
  TrendingUp, BarChart3, Trophy, Quote, Zap,
} from "lucide-react";

const VIKAS_IMG = "https://customer-assets.emergentagent.com/job_saavi-agency/artifacts/r2o26p5o_vs.jpg";

const STATS = [
  { v: "22+", l: "Years of growth marketing leadership", c: "saavi-blue", icon: Briefcase },
  { v: "300K+", l: "Qualified leads delivered", c: "saavi-red", icon: Target },
  { v: "130%", l: "Avg. lead target growth", c: "saavi-green", icon: TrendingUp },
  { v: "35%", l: "Lower cost per lead", c: "saavi-blue", icon: BarChart3 },
];

const EXPERTISE = [
  { label: "Growth Marketing", color: "saavi-blue" },
  { label: "Performance Marketing", color: "saavi-red" },
  { label: "Lead Generation", color: "saavi-green" },
  { label: "Revenue Growth", color: "saavi-blue" },
  { label: "Marketing Automation", color: "saavi-red" },
  { label: "Brand Strategy", color: "saavi-green" },
  { label: "Funnel Optimization", color: "saavi-blue" },
  { label: "Education Marketing", color: "saavi-red" },
];

const TIMELINE = [
  { year: "2002", title: "Started in Digital", desc: "Entered the digital marketing industry when banner ads still ruled the web.", color: "saavi-blue" },
  { year: "2008", title: "Performance Era", desc: "Pivoted to performance marketing — built early lead-gen funnels across Google & display.", color: "saavi-red" },
  { year: "2014", title: "Education Specialist", desc: "Scaled enrolment campaigns for universities, generating 300K+ qualified student leads.", color: "saavi-green" },
  { year: "2020", title: "Automation & Funnels", desc: "Built end-to-end automated growth systems combining ads, CRM, and nurturing.", color: "saavi-blue" },
  { year: "Today", title: "Founder — Saavi Digitex", desc: "Building a growth marketing agency that combines creativity, data, and predictable revenue.", color: "saavi-red" },
];

export default function Team() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="The Team · Founder"
        title={<>Meet the <span className="text-saavi-blue">strategist</span> behind your growth.</>}
        description="Saavi Digitex is founded and led by one of India's most experienced growth marketing professionals."
      />

      {/* HERO PORTRAIT + INTRO */}
      <section className="relative border-b border-border overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 ai-grid opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="blob bg-saavi-blue/25 w-[500px] h-[500px] top-[-150px] left-[-150px]" />
        <div aria-hidden="true" className="blob bg-saavi-red/15 w-[400px] h-[400px] bottom-[-120px] right-[-120px]" style={{ animationDelay: "-9s" }} />

        <div className="container-saavi relative grid grid-cols-1 lg:grid-cols-12 gap-12 py-16 md:py-24 items-center">
          <Reveal variant="scale-in" className="lg:col-span-5">
            <div className="relative mx-auto max-w-md">
              {/* Decorative orbit rings around the portrait */}
              <div className="ai-orbit w-[120%] aspect-square -translate-x-[8%] -translate-y-[8%] top-0 left-0" />
              <div className="ai-orbit ai-orbit-reverse w-[140%] aspect-square -translate-x-[18%] -translate-y-[18%] top-0 left-0" />

              {/* Soft glow halo */}
              <div className="absolute inset-0 -m-6 bg-saavi-blue/15 rounded-full blur-3xl pointer-events-none" />

              <div className="relative ai-card border border-border rounded-md overflow-hidden bg-card">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={VIKAS_IMG}
                    alt="Vikas Savant"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
                {/* Corner brackets */}
                <span className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-saavi-red" aria-hidden="true" />
                <span className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-saavi-blue" aria-hidden="true" />
                <span className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-saavi-green" aria-hidden="true" />
                <span className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-saavi-blue" aria-hidden="true" />

                {/* Live indicator chip */}
                <div className="absolute bottom-3 left-3 ai-pill font-mono-overline px-3 py-1.5 rounded-md flex items-center gap-2 text-foreground">
                  <span className="inline-flex h-1.5 w-1.5 rounded-full bg-saavi-green pulse-dot" />
                  Available for new growth projects
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal variant="fade-up" delay={120} className="lg:col-span-7">
            <div className="font-mono-overline text-saavi-blue mb-4 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Founder · Growth Strategist
            </div>
            <h2 className="text-4xl md:text-6xl">
              <span className="block">Vikas</span>
              <span className="block ai-gradient-text">Savant</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Vikas brings <span className="text-foreground font-semibold">22+ years</span> of leadership in Growth Marketing, Performance Marketing, Lead Generation and Revenue Growth. He has scaled marketing engines for educational institutions, enterprises, and ambitious challenger brands across India.
            </p>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              At Saavi Digitex, he leads strategy on every account — combining a data-first mindset with creative storytelling to turn marketing into a predictable revenue channel.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="mailto:hello@saavidigitex.com">
                <Button size="lg" className="h-12 px-6 gap-2 group" data-testid="team-cta-email">
                  Email Vikas <Mail className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="h-12 px-6 gap-2 group" data-testid="team-cta-book">
                  Book a strategy call <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </Link>
              <a href="https://www.linkedin.com/in/vikassavant/" target="_blank" rel="noreferrer" aria-label="Vikas Savant on LinkedIn" data-testid="team-linkedin-vikas" className="inline-flex h-12 w-12 items-center justify-center rounded-md border border-border hover:border-saavi-blue hover:text-saavi-blue transition-colors">
                <Linkedin className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-10 flex items-center gap-2 text-xs text-muted-foreground font-mono-overline">
              <span className="inline-flex items-center gap-1">
                <span className="ai-dot text-saavi-blue" />
                <span className="ai-dot text-saavi-red" />
                <span className="ai-dot text-saavi-green" />
              </span>
              Strategic intake · data audit · roadmap delivery in 14 days
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS GRID */}
      <section className="border-b border-border">
        <div className="container-saavi grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border">
          {STATS.map(({ v, l, c, icon: Icon }, i) => (
            <Reveal key={l} variant="fade-up" delay={i * 110}>
              <div className="p-8 md:p-10 group relative overflow-hidden">
                <Icon className={`h-5 w-5 text-${c} mb-3 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`} />
                <div className={`text-4xl md:text-5xl font-extrabold tracking-tight text-${c}`}>
                  <AnimatedNumber value={v} duration={1600 + i * 220} />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO QUOTE */}
      <section className="section-pad border-b border-border relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="container-saavi relative">
          <Reveal variant="fade-up">
            <div className="ai-card border border-border rounded-md p-10 md:p-16 bg-card max-w-4xl mx-auto">
              <Quote className="h-10 w-10 text-saavi-red mb-6" />
              <blockquote className="text-2xl md:text-4xl font-extrabold tracking-tight leading-tight">
                "Marketing isn't about chasing clicks. It's about engineering a predictable system that turns curiosity into customers — month after month."
              </blockquote>
              <div className="mt-8 flex items-center gap-4">
                <img src={VIKAS_IMG} alt="Vikas Savant" className="h-12 w-12 rounded-full object-cover border border-border" />
                <div>
                  <div className="font-semibold">Vikas Savant</div>
                  <div className="text-xs font-mono-overline text-muted-foreground">Founder · Saavi Digitex</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPERTISE GRID */}
      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <Reveal variant="fade-up">
            <div className="font-mono-overline text-saavi-blue mb-4 flex items-center gap-2">
              <Zap className="h-3.5 w-3.5" />
              Core Expertise
            </div>
            <h2 className="text-4xl md:text-5xl max-w-2xl">Areas Vikas has shaped — and scaled.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {EXPERTISE.map(({ label, color }, i) => (
              <Reveal key={label} variant="fade-up" delay={i * 60}>
                <div className={`ai-card lift-on-hover border border-border rounded-md p-5 bg-card text-sm font-semibold hover:border-${color} h-full`}>
                  <span className={`inline-block h-1.5 w-6 rounded-full bg-${color} mb-3`} />
                  <div>{label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <Reveal variant="fade-up">
            <div className="font-mono-overline text-saavi-green mb-4 flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5" />
              Journey
            </div>
            <h2 className="text-4xl md:text-5xl max-w-3xl">Two decades. One mission — building growth engines that compound.</h2>
          </Reveal>

          <div className="mt-14 relative">
            {/* Vertical spine */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-border" />
            <ul className="space-y-10 md:space-y-16">
              {TIMELINE.map((t, i) => {
                const isRight = i % 2 === 0;
                return (
                  <li key={t.year} className="md:grid md:grid-cols-2 md:gap-12 relative">
                    <Reveal variant={isRight ? "slide-left" : "slide-right"} className={isRight ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"}>
                      <div className="ai-card relative border border-border rounded-md p-6 bg-card">
                        <div className={`font-mono-overline text-${t.color}`}>{t.year}</div>
                        <h3 className="mt-2 text-2xl">{t.title}</h3>
                        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
                      </div>
                    </Reveal>
                    {/* Center dot */}
                    <span className={`hidden md:block absolute left-1/2 top-6 -translate-x-1/2 h-3 w-3 rounded-full bg-${t.color} ring-4 ring-background`} aria-hidden="true" />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-saavi">
          <Reveal variant="scale-in">
            <div className="relative overflow-hidden border border-border rounded-md bg-card p-10 md:p-16 ai-card">
              <div aria-hidden="true" className="blob bg-saavi-blue/25 w-64 h-64 top-[-100px] right-[-100px]" />
              <div aria-hidden="true" className="blob bg-saavi-red/15 w-64 h-64 bottom-[-100px] left-[-100px]" style={{ animationDelay: "-8s" }} />
              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="font-mono-overline text-saavi-blue mb-4">Work directly with Vikas</div>
                  <h3 className="text-3xl md:text-4xl">Ready to design your growth engine?</h3>
                  <p className="mt-3 text-muted-foreground max-w-lg">Senior strategist. Direct involvement. Outcomes you can measure.</p>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2 group">Book a Free Call <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Button></Link>
                  <Link to="/packages"><Button size="lg" variant="outline" className="h-12 px-6">View Packages</Button></Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
